/**
 * 관리자 인덱스 — 선택한 행을 이전/다음/임의 페이지로 보내는 툴바.
 *
 * 마크업 계약 (index-toolbar.php 와 동기):
 *   <div data-bulk-target-scope
 *        data-move-url="/console/site/works/move"   ← POST /resources/move (body: ids[], mode, ...)
 *        data-current-page="1"
 *        data-last-page="5"
 *        data-per-page="20">
 *     <button data-page-move="prev">…</button>
 *     <input  data-page-move-input type="number" min="1" max="5">
 *     <button data-page-move="jump">…</button>
 *     <button data-page-move="next">…</button>
 *   </div>
 *
 *   체크박스(`[data-bulk-row]`) 는 같은 scope 의 `[data-bulk-target]` 테이블에 있다.
 *
 * RESTful: 다중 선택 이동은 POST /resources/move  body JSON: { ids, mode, per_page, source_page, target_page? }
 * 자동 부착: GlobalApplication 의 admin 분기에서 initPageMove() 한 번 호출.
 */
import { Modal } from "../../utils/Modal";
import { Toast } from "../../utils/Toast";

interface MoveResponse {
    result?: string;
    moved?: number;
    redirect_page?: number;
    msg?: string;
}

function getCsrf(scope: HTMLElement): string {
    const meta = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]');
    if (meta?.content) return meta.content;
    const input = scope.querySelector<HTMLInputElement>('input[name="_token"]');
    if (input?.value) return input.value;
    return document.querySelector<HTMLInputElement>('input[name="_token"]')?.value ?? "";
}

export class PageMoveTool {
    readonly #scope: HTMLElement;
    #table: HTMLTableElement | null = null;
    #busy = false;

    constructor(scope: HTMLElement) {
        this.#scope = scope;
    }

    init(): this {
        this.#table = this.#findTable();
        if (!this.#table || !this.#scope.dataset.moveUrl) {
            return this;
        }

        const prev = this.#scope.querySelector<HTMLButtonElement>('[data-page-move="prev"]');
        const next = this.#scope.querySelector<HTMLButtonElement>('[data-page-move="next"]');
        const jump = this.#scope.querySelector<HTMLButtonElement>('[data-page-move="jump"]');

        prev?.addEventListener("click", () => void this.#move("prev"));
        next?.addEventListener("click", () => void this.#move("next"));
        jump?.addEventListener("click", () => void this.#move("page"));

        // 체크 상태가 바뀌면 버튼 활성화 동기화
        this.#table.addEventListener("change", (e) => {
            const t = e.target as HTMLElement | null;
            if (t instanceof HTMLInputElement && (t.matches("[data-bulk-row]") || t.matches("[data-bulk-master]"))) {
                this.#syncButtons();
            }
        });
        // 첫 진입 시 버튼 동기화
        this.#syncButtons();

        return this;
    }

    #findTable(): HTMLTableElement | null {
        const resource = this.#scope.dataset.bulkResource ?? "";
        if (resource) {
            try {
                const escaped = window.CSS && typeof window.CSS.escape === "function"
                    ? window.CSS.escape(resource)
                    : resource.replace(/"/g, '\\"');
                const t = document.querySelector<HTMLTableElement>(
                    `table[data-bulk-target][data-bulk-resource="${escaped}"]`,
                );
                if (t) return t;
            } catch {
                // fall through
            }
        }
        // 형제 트리 fallback
        let parent: HTMLElement | null = this.#scope.parentElement;
        while (parent && parent !== document.body) {
            const t = parent.querySelector<HTMLTableElement>("table[data-bulk-target]");
            if (t) return t;
            parent = parent.parentElement;
        }
        return null;
    }

    #selectedIds(): number[] {
        if (!this.#table) return [];
        const rows = Array.from(this.#table.querySelectorAll<HTMLInputElement>("tbody [data-bulk-row]"));
        const ids: number[] = [];
        rows.forEach((cb) => {
            if (!cb.checked) return;
            const id = Number(cb.value || "0") || 0;
            if (id > 0) ids.push(id);
        });
        return ids;
    }

    #syncButtons(): void {
        const hasAny = this.#selectedIds().length > 0;
        const currentPage = Number(this.#scope.dataset.currentPage ?? "1") || 1;
        const lastPage = Number(this.#scope.dataset.lastPage ?? "1") || 1;

        const prev = this.#scope.querySelector<HTMLButtonElement>('[data-page-move="prev"]');
        const next = this.#scope.querySelector<HTMLButtonElement>('[data-page-move="next"]');
        const jump = this.#scope.querySelector<HTMLButtonElement>('[data-page-move="jump"]');

        if (prev) prev.disabled = !hasAny || currentPage <= 1 || this.#busy;
        if (next) next.disabled = !hasAny || currentPage >= lastPage || this.#busy;
        if (jump) jump.disabled = !hasAny || lastPage <= 1 || this.#busy;
    }

    async #move(mode: "prev" | "next" | "page"): Promise<void> {
        if (this.#busy) return;
        const ids = this.#selectedIds();
        if (ids.length === 0) return;

        const moveUrl = this.#scope.dataset.moveUrl ?? "";
        if (!moveUrl) {
            Toast.error("이동 엔드포인트가 설정되지 않았습니다.");
            return;
        }

        const perPage = Number(this.#scope.dataset.perPage ?? "20") || 20;
        const currentPage = Number(this.#scope.dataset.currentPage ?? "1") || 1;
        const lastPage = Number(this.#scope.dataset.lastPage ?? "1") || 1;

        let targetPage: number | null = null;
        if (mode === "page") {
            const input = this.#scope.querySelector<HTMLInputElement>("[data-page-move-input]");
            const raw = Number(input?.value ?? "0") || 0;
            if (raw < 1) {
                await Modal.alert("이동할 페이지 번호를 입력하세요.");
                return;
            }
            targetPage = Math.max(1, Math.min(lastPage, raw));
            if (targetPage === currentPage) {
                await Modal.alert("이미 같은 페이지입니다.");
                return;
            }
        }

        this.#busy = true;
        this.#syncButtons();

        const csrf = getCsrf(this.#scope);
        const payload: Record<string, unknown> = {
            ids,
            mode,
            per_page: perPage,
            source_page: currentPage,
        };
        if (targetPage !== null) payload.target_page = targetPage;

        try {
            const res = await fetch(moveUrl, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                    "X-Requested-With": "XMLHttpRequest",
                    ...(csrf ? { "X-CSRF-TOKEN": csrf } : {}),
                },
                body: JSON.stringify(payload),
                credentials: "same-origin",
            });

            const raw = await res.text();
            let data: MoveResponse = {};
            try {
                data = raw ? (JSON.parse(raw) as MoveResponse) : {};
            } catch {
                data = {};
            }

            if (!res.ok || data.result !== "success") {
                Toast.error(data.msg || "페이지 이동에 실패했습니다.");
                this.#busy = false;
                this.#syncButtons();
                return;
            }

            Toast.success(data.msg || `${data.moved ?? ids.length}건 이동했습니다.`);
            const redirectPage = Math.max(1, Number(data.redirect_page ?? currentPage) || currentPage);
            const url = new URL(window.location.href);
            url.searchParams.set("page", String(redirectPage));
            window.setTimeout(() => window.location.assign(url.toString()), 350);
        } catch {
            Toast.error("네트워크 오류로 이동을 완료하지 못했습니다.");
            this.#busy = false;
            this.#syncButtons();
        }
    }
}

/** 페이지 내 모든 [data-page-move-scope] 에 대해 PageMoveTool 자동 부착 */
export function initPageMove(scope: HTMLElement = document.body): void {
    const scopes = Array.from(scope.querySelectorAll<HTMLElement>("[data-bulk-target-scope][data-move-url]"));
    scopes.forEach((el) => new PageMoveTool(el).init());
}
